import { HealthAnswer } from "../../../model/members/HealthFormSummary";
import { QuestionnaireSchema } from "./QuestionnaireSchema";

export interface FlaggedAnswer {
    key: string;
    label: string;
    answer: string;
}

// Compute which answers raise an operator flag, by comparing each answer to its
// field's flagWhen in the schema. Handles checkboxGroup answers (pipe-joined):
// a flag fires if any selected value matches flagWhen. Used to alert reception
// on the health tab (e.g. heartCondition === "yes").
export function computeFlaggedAnswers(
    schema: QuestionnaireSchema,
    answers: HealthAnswer[],
): FlaggedAnswer[] {
    const answerByKey = new Map(answers.map((a) => [a.question, a.answer]));
    const flagged: FlaggedAnswer[] = [];
    for (const section of schema.sections) {
        for (const field of section.fields) {
            if (field.flagWhen == null) continue;
            const answer = answerByKey.get(field.key);
            if (answer == null || answer === '') continue;
            const flagValues = Array.isArray(field.flagWhen) ? field.flagWhen : [field.flagWhen];
            const selected = answer.split('|');
            if (flagValues.some((fv) => selected.includes(fv))) {
                flagged.push({ key: field.key, label: field.label, answer });
            }
        }
    }
    return flagged;
}

// Whether a health submission is actually populated. A blank/untouched
// questionnaire still serialises to a full answer list — radios/text become ""
// and unticked booleans become "false" — so the mere presence of a submission
// (submissionId set, answers non-empty) does NOT mean a questionnaire is on file.
// It counts as populated only if at least one answer carries a real value.
//
// Heuristic assumption: a *substantive* answer is never the literal string
// "false". This holds for the bundled PARQ (its questions are yes/no radios that
// serialise to "no", and the only boolean is the consent `agreement` checkbox
// whose unticked state — "false" — is genuinely "not completed"). An
// operator-authored custom schema whose only substantive question is a boolean a
// member deliberately declined would read as unpopulated; acceptable today, but
// keep this coupling in mind before adding boolean-only questionnaires.
export function isHealthQuestionnairePopulated(answers: HealthAnswer[]): boolean {
    return answers.some((a) => a.answer != null && a.answer !== "" && a.answer !== "false");
}

// Label lookup for rendering answers against the schema; falls back to the raw
// key when the schema has no matching field (e.g. a legacy answer key).
export function buildFieldLabelMap(schema: QuestionnaireSchema): Map<string, string> {
    const map = new Map<string, string>();
    for (const section of schema.sections) {
        for (const field of section.fields) {
            map.set(field.key, field.label);
        }
    }
    return map;
}
